/**
 * Defines an web widget that can be used to choose the dimensions of a grid
 *
 * @module MW/GridPicker
 * @copyright Copyright 2015 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/dom",
    "dojo/Evented",
    "dojo/keys",
    "dojo/on",
    "dojo/touch",
    "dijit/_WidgetBase"
], function (declare, event, lang, dom, Evented, keys, on, touch, _WidgetBase) {

    var ROW_MAJOR = "rowMajor";
    var COLUMN_MAJOR = "columnMajor";
    var SELECTION_CHANGED = "gridSelectionChanged";

    /**
     * @class MW/GridPicker
     * @classdesc Defines a widget that displays a grid of boxes and allows the user to choose a number of rows and
     * columns by clicking within this grid.
     */
    var GridPicker = declare([_WidgetBase, Evented], {

        maxRows: 5,
        maxColumns: 5,
        occupancy: 0,
        fillOrder: ROW_MAJOR,

        _highlightedRows: 0,
        _highlightedColumns: 0,
        _lastSelectedRows: 0,
        _lastSelectedColumns: 0,
        // _cells

        buildRendering: function () {
            this.inherited(arguments);
            this.domNode.classList.add("gridPicker");
            this.domNode.setAttribute("tabindex", 0);
            this._createGrid();
        },

        postCreate: function () {
            this.inherited(arguments);

            this.domNode.addEventListener("mousemove", this._highlightSelection.bind(this), true);
            this.domNode.addEventListener("click", this._commitSelection.bind(this));
            this.domNode.addEventListener("mouseleave", this._clearHighlighting.bind(this));
        },

        getLastSelectedRowCount: function () {
            return this._lastSelectedRows;
        },

        getLastSelectedColumnCount: function () {
            return this._lastSelectedColumns;
        },

        navigate: function (event) {
            var selectedCells = false;
            var result = false;
            if (this._highlightedRows !== 0 && this._highlightedColumns !== 0) {
                selectedCells = true;
            }

            switch (event.keyCode) {

            case keys.DOWN_ARROW:
                if (!selectedCells) {
                    this.highlightCells(0, 0);
                } else {
                    this.highlightCells(this._highlightedRows, this._highlightedColumns - 1);
                }
                result = true;
                break;

            case keys.UP_ARROW:
                if (selectedCells) {
                    this.highlightCells(this._highlightedRows - 2, this._highlightedColumns - 1);
                }
                result = true;
                break;

            case keys.RIGHT_ARROW:
                if (selectedCells) {
                    this.highlightCells(this._highlightedRows - 1, this._highlightedColumns);
                }
                result = true;
                break;

            case keys.LEFT_ARROW:
                if (selectedCells) {
                    this.highlightCells(this._highlightedRows - 1, this._highlightedColumns - 2);
                }
                result = true;
                break;

            case keys.ENTER:
                this._commitSelection();
                this._clearHighlighting();
                result = true;
                break;

            case keys.TAB:
                if (!selectedCells) {
                    this.highlightCells(0, 0);
                } else {
                    if (this._highlightedColumns < this.maxColumns) {
                        this.highlightCells(this._highlightedRows - 1, this._highlightedColumns);
                    } else if (this._highlightedRows < this.maxRows) {
                        this.highlightCells(this._highlightedRows, 0);
                    }
                }
                result = true;
                break;

            case keys.ESCAPE:
                this._clearHighlighting();
            }

            return result;
        },

        highlightCells: function (selectedRow, selectedCol) {

            var row, col, count = 0;
            if (selectedRow !== undefined && selectedCol !== undefined) {
                this._highlightedRows = selectedRow + 1;
                this._highlightedColumns = selectedCol + 1;
                if (this.fillOrder === ROW_MAJOR) {
                    for (row = 0; row < this.maxRows; row++) {
                        for (col = 0; col < this.maxColumns; col++) {
                            if (row <= selectedRow && col <= selectedCol) {
                                this._cells[row][col].classList.add("selected");
                                if (count < this.occupancy) {
                                    this._cells[row][col].classList.add("occupied");
                                } else {
                                    this._cells[row][col].classList.remove("occupied");
                                }
                                count++;
                            } else {
                                this._cells[row][col].classList.remove("selected");
                            }
                        }
                    }
                } else {
                    for (col = 0; col < this.maxColumns; col++) {
                        for (row = 0; row < this.maxRows; row++) {
                            if (row <= selectedRow && col <= selectedCol) {
                                this._cells[row][col].classList.add("selected");
                                if (count < this.occupancy) {
                                    this._cells[row][col].classList.add("occupied");
                                } else {
                                    this._cells[row][col].classList.remove("occupied");
                                }
                                count++;
                            } else {
                                this._cells[row][col].classList.remove("selected");
                            }
                        }
                    }
                }
            }
        },

        _createGrid: function () {
            var row, col, cell;
            this._cells = [];
            this._cells.length = this.maxRows;
            for (row = 0; row < this.maxRows; row++) {
                this._cells[row] = [];
                this._cells[row].length = this.maxColumns;
                for (col = 0; col < this.maxColumns; col++) {
                    //Constructing the grid cells as input(button) elements instead of divs.
                    //This is done to enable click events on touch devices.
                    // Dojo's popup.js currently has a workaround in its code that prevents touchend event from firing
                    // except in the case of input/button elements.
                    cell = document.createElement("button");
                    cell.setAttribute("id", this.id + "Cell" + row + "_" + col);
                    cell.setAttribute("tabindex", "0");

                    this.domNode.appendChild(cell);

                    cell.classList.add("cell");
                    cell.row = row;
                    cell.col = col;
                    this._cells[row][col] = cell;
                }
                if (row < this.maxRows) {
                    var br = document.createElement("br");
                    this.domNode.appendChild(br);

                }
            }
        },

        _highlightSelection: function (e) {
            var selectedRow = e.target.row;
            var selectedCol = e.target.col;

            this.highlightCells(selectedRow, selectedCol);
        },

        _clearHighlighting: function () {
            var row, col;
            for (row = 0; row < this._highlightedRows; row++) {
                for (col = 0; col < this._highlightedColumns; col++) {
                    this._cells[row][col].classList.remove("selected");
                }
            }
            this._highlightedRows = 0;
            this._highlightedColumns = 0;
        },

        _commitSelection: function () {
            this._lastSelectedRows = this._highlightedRows;
            this._lastSelectedColumns = this._highlightedColumns;

            this.emit(SELECTION_CHANGED, {
                rows: this._lastSelectedRows,
                columns: this._lastSelectedColumns
            });
        },

        startup: function () {
            this.inherited(arguments);
        },

        destroy: function() {
            this.domNode.removeEventListener("mousemove", this._highlightSelection.bind(this), true);
            this.domNode.removeEventListener("click", this._commitSelection.bind(this));
            this.domNode.removeEventListener("mouseleave", this._clearHighlighting.bind(this));
            this.inherited(arguments);
        }
    });

    GridPicker.ROW_MAJOR = ROW_MAJOR;
    GridPicker.COLUMN_MAJOR = COLUMN_MAJOR;
    GridPicker.SELECTION_CHANGED = SELECTION_CHANGED;

    return GridPicker;
});
